import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Button, Input, Space, Typography, message, Card, Tag } from 'antd';
import moment from 'moment';
import { approvalService } from '@iso/services';

const { TextArea } = Input;
const { Text } = Typography;

const STATUS_LABEL = {
  approved: 'Approved',
  rejected: 'Rejected',
};

const ApprovalSection = ({ model, objectId, latestApproval, onApprovalUpdated, canDecide: canDecideProp }) => {
  const [currentApproval, setCurrentApproval] = useState(latestApproval || null);
  const [loading, setLoading] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [comment, setComment] = useState('');
  const activePermission = useSelector((state) => state.permission?.activePermission);
  const canDecide = canDecideProp !== undefined ? canDecideProp : ['owner', 'admin'].includes(activePermission);

  useEffect(() => {
    setCurrentApproval(latestApproval || null);
  }, [latestApproval]);

  const resolveStatusMeta = (approval) => {
    if (!approval) {
      return {
        label: 'Pending decision',
        color: 'default',
        by: null,
        date: null,
        comment: null,
      };
    }

    const label = STATUS_LABEL[approval.status] || approval.status;
    const color = approval.status === 'approved' ? 'green' : approval.status === 'rejected' ? 'red' : 'default';
    return {
      label,
      color,
      by: approval.decided_by_name || 'Unknown user',
      date: approval.decided_at ? moment(approval.decided_at).format('DD/MM/YYYY HH:mm') : null,
      comment: approval.comment || null,
    };
  };

  const renderSummaryCard = () => {
    const meta = resolveStatusMeta(currentApproval);

    return (
      <Card size="small" bordered={false} style={{ background: '#F5F7FA' }}>
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Text strong>Latest decision</Text>
          <Space wrap>
            <Tag color={meta.color}>{meta.label}</Tag>
            {meta.by && <Text type="secondary">by {meta.by}</Text>}
            {meta.date && <Text type="secondary">on {meta.date}</Text>}
          </Space>
          {meta.comment && (
            <Text italic style={{ display: 'block' }}>
              “{meta.comment}”
            </Text>
          )}
          {meta.label === 'Pending decision' && (
            <Text type="secondary">No approval recorded yet.</Text>
          )}
        </Space>
      </Card>
    );
  };

  const handleSuccess = (response, verb) => {
    const approval = response.approval;
    setCurrentApproval(approval);
    setRejecting(false);
    setComment('');
    message.success(`Record ${verb} successfully.`);
    if (typeof onApprovalUpdated === 'function') {
      onApprovalUpdated(approval);
    }
  };

  const handleApprove = async () => {
    if (!objectId || loading || !canDecide) return;
    setLoading(true);
    try {
      const response = await approvalService.create({
        model,
        objectId,
        status: 'approved',
      });
      if (response?.code === '0000' && response.approval) {
        handleSuccess(response, 'approved');
      } else {
        throw new Error(response?.message || 'Failed to approve record.');
      }
    } catch (error) {
      message.error(error.message || 'Failed to approve record.');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectPrompt = () => {
    if (!canDecide) {
      return;
    }
    setRejecting(true);
  };

  const handleCancelReject = () => {
    if (loading) return;
    setRejecting(false);
    setComment('');
  };

  const submitRejection = async () => {
    if (!objectId || loading || !canDecide) return;
    if (!comment.trim()) {
      message.warning('Please provide a rejection comment.');
      return;
    }
    setLoading(true);
    try {
      const response = await approvalService.create({
        model,
        objectId,
        status: 'rejected',
        comment: comment.trim(),
      });
      if (response?.code === '0000' && response.approval) {
        handleSuccess(response, 'rejected');
      } else {
        throw new Error(response?.message || 'Failed to reject record.');
      }
    } catch (error) {
      message.error(error.message || 'Failed to reject record.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="health-safety-approval-section">
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {renderSummaryCard()}
        <Space wrap>
          <Button
            type="primary"
            className="hs-approval-btn-primary"
            onClick={handleApprove}
            loading={loading}
            disabled={!objectId || !canDecide}
          >
            Approve
          </Button>
          {!rejecting && (
            <Button
              danger
              onClick={handleRejectPrompt}
              disabled={!objectId || !canDecide}
            >
              Reject
            </Button>
          )}
        </Space>
        {rejecting && (
          <Card size="small" bordered={false} style={{ background: '#fff' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <Text strong>Reason for rejection</Text>
              <TextArea
                rows={3}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                placeholder="Describe why this record is being rejected"
                disabled={loading || !canDecide}
              />
              <Space>
                <Button
                  type="primary"
                  danger
                  onClick={submitRejection}
                  loading={loading}
                  disabled={!comment.trim() || !canDecide}
                >
                  Submit rejection
                </Button>
                <Button onClick={handleCancelReject} disabled={loading}>
                  Cancel
                </Button>
              </Space>
            </Space>
          </Card>
        )}
      </Space>
    </div>
  );
};

ApprovalSection.propTypes = {
  model: PropTypes.oneOf(['incident', 'hazard', 'observation', 'safety_walk', 'health_safety'])
    .isRequired,
  objectId: PropTypes.number,
  latestApproval: PropTypes.shape({
    id: PropTypes.number,
    status: PropTypes.string,
    decided_by_name: PropTypes.string,
    decided_at: PropTypes.string,
    comment: PropTypes.string,
  }),
  onApprovalUpdated: PropTypes.func,
};

ApprovalSection.defaultProps = {
  objectId: null,
  latestApproval: null,
  onApprovalUpdated: null,
};

export default ApprovalSection;
